'use client';
import React, { useEffect, useState } from 'react';
import { RelatedProducts as SharedRelatedProducts } from '@launchstore/shared-puck';
import type { RelatedProductsProps } from '@launchstore/shared-puck';
import { useProduct } from '@/lib/hooks/useProduct';
import { getRegion } from '@/lib/data/regions';
import { getMedusaPublishableKey } from '@/lib/config';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import TemplateProductCard from '@/modules/products/components/product-preview/template-product-card';
import { listProducts } from '@/lib/data/products';
import type { CarouselProduct } from '@launchstore/shared-puck';

const toCarouselProduct = (p: any): CarouselProduct => ({
  id: p.id,
  title: p.title,
  handle: p.handle,
  thumbnail: p.thumbnail,
  images: p.images,
  variants: p.variants,
});

const fetchByRelation = async (relatedBy: string, currentProduct: any, region: any, countryCode: string, productIdsStr: string, max: number) => {
  const queryParams: any = { region_id: region.id, limit: max + 1, fields: '*variants.calculated_price' };
  if (relatedBy === 'collection') {
    const cid = currentProduct?.collection_id;
    if (cid) queryParams.collection_id = [cid];
  } else if (relatedBy === 'tags') {
    if (currentProduct?.tags?.length) queryParams.tag_id = currentProduct.tags.map((t: any) => t.id);
  } else if (relatedBy === 'category') {
    const cid = currentProduct?.categories?.[0]?.id;
    if (cid) queryParams.category_id = [cid];
  } else if (relatedBy === 'manual' || relatedBy === 'upsell' || relatedBy === 'crosssell' || relatedBy === 'frequently_bought') {
    if (relatedBy === 'manual' && productIdsStr) {
      const ids = productIdsStr.split(',').map((s) => s.trim()).filter(Boolean);
      if (ids.length > 0) queryParams.id = ids;
    } else if (relatedBy !== 'manual') {
      // upsell/crosssell/frequently_bought: hit Medusa recommendations endpoint
      const medusaUrl = process.env.NEXT_PUBLIC_MEDUSA_BACKEND_URL || 'http://localhost:9000';
      try {
        const res = await fetch(
          `${medusaUrl}/store/products/${currentProduct?.id}/recommendations?type=${relatedBy}&limit=${max}®ion_id=${region.id}`,
          {
            headers: { 'x-publishable-key': getMedusaPublishableKey() || '' },
            next: { revalidate: 300 },
          }
        );
        if (res.ok) {
          const data = await res.json();
          return (data.recommendations || []) as any[];
        }
      } catch {}
      // Fallback to collection if no recommendations
      if (currentProduct?.collection_id) {
        queryParams.collection_id = [currentProduct.collection_id];
      }
    }
  }
  const { response: _lr } = await listProducts({ queryParams, countryCode });
  return _lr.products || [];
};

const SharedRelatedProductsRender = (SharedRelatedProducts as any).render;

const RelatedProductsImpl: React.FC<RelatedProductsProps> = (props: any) => {
  const { product, region, countryCode } = useProduct() as any;
  const { context } = usePuckContext() as any;
  const cardTemplate = context?.productCardTemplate;
  const [reg, setReg] = useState<any>(region || null);
  const [products, setProducts] = useState<CarouselProduct[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!product || !countryCode) { setLoaded(true); return; }
    let mounted = true;
    (async () => {
      try {
        const reg = region || await getRegion(countryCode);
        if (mounted && reg && !region) setReg(reg);
        if (!reg) { setLoaded(true); return; }
        const fetched = await fetchByRelation(props.relatedBy, product, reg, countryCode, props.productIds || '', props.maxProducts || 8);
        const filtered = (fetched || []).filter((p: any) => p.id !== product.id).slice(0, props.maxProducts || 8);
        if (mounted) setProducts(filtered.map(toCarouselProduct));
      } catch {}
      if (mounted) setLoaded(true);
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, countryCode, region?.id, props.relatedBy, props.productIds, props.maxProducts]);

  if (!loaded) return <></>;
  const renderProduct =
    cardTemplate && reg
      ? (p: any) => (
          <TemplateProductCard
            product={p as any}
            region={reg}
            productCardTemplate={cardTemplate}
            countryCode={countryCode}
          />
        )
      : undefined;
  return (
    <SharedRelatedProductsRender {...(props as any)} products={products} renderProduct={renderProduct} />
  );
};

export const RelatedProducts: any = { ...SharedRelatedProducts, render: (props: any) => <RelatedProductsImpl {...props} /> };
export default RelatedProducts;
